/**
 * Dashboard Layout Store — Founder-configurable widget grid
 * Persisted to localStorage. Founder can resize, reorder, hide/show widgets.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type WidgetSize = 'compact' | 'normal' | 'large' | 'hero';

export interface WidgetConfig {
  id:      string;
  label:   string;
  visible: boolean;
  size:    WidgetSize;
  order:   number;
}

const DEFAULTS: WidgetConfig[] = [
  { id: 'engine-of-day',   label: 'Engine of the Day',         visible: true,  size: 'normal', order: 0 },
  { id: 'continue',        label: 'Continue Where Left Off',   visible: true,  size: 'normal', order: 1 },
  { id: 'live-metrics',    label: 'Live Metrics',              visible: true,  size: 'normal', order: 2 },
  { id: 'quick-launch',    label: 'Quick Launch Grid',         visible: true,  size: 'normal', order: 3 },
  { id: 'whats-new',       label: "What's New",                visible: true,  size: 'normal', order: 4 },
  { id: 'heat-map',        label: 'Global Operations Map',     visible: true,  size: 'normal', order: 5 },
  { id: 'night-ops',       label: 'Night Ops',                 visible: true,  size: 'normal', order: 6 },
  { id: 'submittal',       label: 'Submittal Generator',       visible: true,  size: 'normal', order: 7 },
  { id: 'context',         label: 'Master Context Ingestor',   visible: true,  size: 'normal', order: 8 },
  { id: 'stat-tiles',      label: 'Platform Stat Tiles',       visible: true,  size: 'normal', order: 9 },
  { id: 'wins-feed',       label: 'Wins Feed',                 visible: true,  size: 'normal', order: 10 },
  { id: 'system-core',     label: 'System Core Diagram',       visible: true,  size: 'normal', order: 11 },
  { id: 'founder-alloc',   label: 'Founder Allocation',        visible: true,  size: 'normal', order: 12 },
  { id: 'audit-trail',     label: 'Audit Trail + Cabling',     visible: true,  size: 'normal', order: 13 },
];

interface LayoutState {
  widgets:    WidgetConfig[];
  editMode:   boolean;
  hasChanges: boolean;

  setEditMode:   (v: boolean) => void;
  setSize:       (id: string, size: WidgetSize) => void;
  toggleVisible: (id: string) => void;
  moveUp:        (id: string) => void;
  moveDown:      (id: string) => void;
  saveLayout:    () => void;
  resetLayout:   () => void;
}

export const useDashboardLayout = create<LayoutState>()(
  persist(
    immer((set, get) => ({
      widgets:    DEFAULTS,
      editMode:   false,
      hasChanges: false,

      setEditMode: (v) => set((s) => { s.editMode = v; }),

      setSize: (id, size) => set((s) => {
        const w = s.widgets.find((w) => w.id === id);
        if (w) { w.size = size; s.hasChanges = true; }
      }),

      toggleVisible: (id) => set((s) => {
        const w = s.widgets.find((w) => w.id === id);
        if (w) { w.visible = !w.visible; s.hasChanges = true; }
      }),

      moveUp: (id) => set((s) => {
        const idx = s.widgets.findIndex((w) => w.id === id);
        if (idx <= 0) return;
        const sorted = [...s.widgets].sort((a, b) => a.order - b.order);
        const pos    = sorted.findIndex((w) => w.id === id);
        if (pos <= 0) return;
        const prev = sorted[pos - 1];
        const curr = sorted[pos];
        if (!prev || !curr) return;
        [prev.order, curr.order] = [curr.order, prev.order];
        s.hasChanges = true;
      }),

      moveDown: (id) => set((s) => {
        const sorted = [...s.widgets].sort((a, b) => a.order - b.order);
        const pos    = sorted.findIndex((w) => w.id === id);
        if (pos >= sorted.length - 1) return;
        const next = sorted[pos + 1];
        const curr = sorted[pos];
        if (!curr || !next) return;
        [curr.order, next.order] = [next.order, curr.order];
        s.hasChanges = true;
      }),

      saveLayout: () => set((s) => { s.hasChanges = false; }),

      resetLayout: () => set((s) => {
        s.widgets    = DEFAULTS.map((d) => ({ ...d }));
        s.hasChanges = false;
      }),
    })),
    {
      name:    'r2:dashboard-layout',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ widgets: s.widgets }),
    },
  ),
);

// ── Helpers ────────────────────────────────────────────────────────────────────
export function useWidget(id: string) {
  return useDashboardLayout((s) => s.widgets.find((w) => w.id === id) ?? DEFAULTS.find((d) => d.id === id)!);
}

/**
 * Returns widgets sorted by order.
 * Uses shallow comparison to avoid infinite re-render loop:
 * the selector returns a NEW array each call, so without shallow
 * Zustand would see a "changed" result every render → infinite loop.
 */
export function useSortedWidgets() {
  // Subscribe to the raw widgets array. When the array reference changes
  // (e.g., on layout mutation), this hook re-renders exactly once.
  const widgets = useDashboardLayout((s) => s.widgets);
  return [...widgets].sort((a, b) => a.order - b.order);
}

/** CSS scale transform per size preset */
export const SIZE_SCALE: Record<WidgetSize, { padding: string; scale: string; label: string }> = {
  compact: { padding: '0',    scale: 'scale(0.97)', label: 'S' },
  normal:  { padding: '0',    scale: 'none',        label: 'M' },
  large:   { padding: '12px', scale: 'none',        label: 'L' },
  hero:    { padding: '24px', scale: 'none',        label: 'XL' },
};
